import { useEffect, useRef, useState } from 'react';
import type { LessonGuide } from '../../shared/guide';
import { blockLabel } from '../nbt/blockLabel';
import { RubyText } from '../guide/ruby';
import { CommandHero } from './CommandHero';
import { ItemChecklist } from './ItemChecklist';

interface GuidePanelProps {
  guide: LessonGuide;
  /** 0=じゅんび、1..N=STEP、N+1=かんせい。 */
  step: number;
  /** ここまでは進んだことがある、というステップ番号。先へのジャンプはこの+1まで。 */
  maxVisited: number;
  checks: Record<string, boolean>;
  thumbnails: Map<string, string>;
  onNavigate: (step: number) => void;
  onToggleCheck: (block: string) => void;
  onReset: () => void;
  onExit: () => void;
}

/**
 * レッスンガイドの本体。広い画面では3Dの横のカラム、狭い画面では
 * 3Dの下から出るボトムシートとして表示する（出し分けはCSS側）。
 */
export function GuidePanel({
  guide,
  step,
  maxVisited,
  checks,
  thumbnails,
  onNavigate,
  onToggleCheck,
  onReset,
  onExit,
}: GuidePanelProps) {
  const total = guide.steps.length;
  const hasItems = guide.items.length > 0;
  const remaining = guide.items.filter((item) => checks[item.block] !== true).length;
  const [collapsed, setCollapsed] = useState(false);
  const [nudge, setNudge] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);
  const touchStartY = useRef<number | null>(null);
  const swiped = useRef(false);

  // ステップが変わったら本文を先頭から読めるようにし、シートも開く。
  useEffect(() => {
    bodyRef.current?.scrollTo({ top: 0 });
    setCollapsed(false);
    setNudge(false);
  }, [step]);

  // チェックを付け直し始めたら、注意表示は役目を終える。
  useEffect(() => {
    setNudge(false);
  }, [checks]);

  const advanceFromItems = () => {
    // 完全ロックにはしない。教室では先生の判断で先へ進みたい場面があるため、
    // 1回目は未チェックを揺らして案内を出し、そのままもう一度押されたら進める。
    if (remaining > 0 && !nudge) {
      setNudge(true);
      return;
    }
    onNavigate(1);
  };

  const handleReset = () => {
    if (window.confirm('さいしょから やりなおす？（チェックと すすみは きえるよ）')) onReset();
  };

  const dotLabel = (target: number) => {
    if (target === 0) return 'じゅんび';
    if (target === total + 1) return 'かんせい';
    return `STEP ${target}`;
  };

  const current = step >= 1 && step <= total ? guide.steps[step - 1] : null;

  return (
    <section className={`guide-panel${collapsed ? ' is-collapsed' : ''}`} aria-label="レッスンガイド">
      <button
        type="button"
        className="guide-handle"
        aria-expanded={!collapsed}
        onClick={() => {
          // スワイプとして処理した直後の合成clickで、逆向きにトグルし直さない。
          if (swiped.current) {
            swiped.current = false;
            return;
          }
          setCollapsed((value) => !value);
        }}
        onTouchStart={(event) => { touchStartY.current = event.touches[0].clientY; }}
        onTouchEnd={(event) => {
          const start = touchStartY.current;
          touchStartY.current = null;
          if (start === null) return;
          const delta = event.changedTouches[0].clientY - start;
          if (Math.abs(delta) > 24) {
            swiped.current = true;
            setCollapsed(delta > 0);
          }
        }}
      >
        <span className="guide-handle-bar" aria-hidden="true" />
        <span className="guide-handle-text">
          {step === 0 ? 'じゅんび' : step > total ? 'かんせい！' : `STEP ${step} / ${total}`}
        </span>
        <span className="guide-handle-hint">{collapsed ? 'ひらく' : 'とじる'}</span>
      </button>

      <nav className="guide-dots" aria-label="ステップの いちらん">
        {Array.from({ length: total + 2 }, (_, target) => {
          // アイテム集めの無いレッスンでは、じゅんび(0)のドット自体を出さない。
          if (target === 0 && !hasItems) return null;
          const reachable = target <= maxVisited + 1;
          return (
            <button
              key={target}
              type="button"
              className={`guide-dot${target === step ? ' is-current' : ''}${target !== step && target <= maxVisited ? ' is-done' : ''}`}
              disabled={!reachable}
              aria-label={dotLabel(target)}
              aria-current={target === step ? 'step' : undefined}
              onClick={() => onNavigate(target)}
            >
              {target === 0 ? 'じ' : target === total + 1 ? '✔' : target}
            </button>
          );
        })}
      </nav>

      <div className="guide-body" ref={bodyRef}>
        {step === 0 && (
          <div className="guide-intro">
            <p className="guide-kicker">きょう つくるもの</p>
            <h3 className="guide-title"><RubyText text={guide.title} /></h3>
            <p className="guide-note">この 3Dが かんせいの すがた。くるくる まわして みてみよう！</p>
            <p className="guide-kicker">きょう つかう 材料（ブロック・どうぐ）</p>
            <p className="guide-note">クリエイティブの インベントリを ひらいて、虫めがね（検索）に 下の名前を 入れよう。</p>
            <ItemChecklist
              items={guide.items}
              checks={checks}
              thumbnails={thumbnails}
              nudge={nudge}
              onToggle={onToggleCheck}
            />
            <p className={`guide-remaining${nudge ? ' is-nudged' : ''}`} aria-live="polite">
              {remaining === 0
                ? 'ぜんぶ あつまった！'
                : nudge
                  ? `あと ${remaining}こ。チェックしないで すすむなら、もういちど「つぎへ」を おしてね`
                  : `あと ${remaining}こ チェックしてね`}
            </p>
          </div>
        )}

        {current && (
          <div className="guide-step">
            <h3 className="guide-title"><RubyText text={current.title} /></h3>
            {current.summary && <p className="guide-summary"><RubyText text={current.summary} /></p>}
            {current.image && <img className="guide-image" src={current.image.src} alt={current.image.alt ?? ''} />}
            {current.items && current.items.length > 0 && (
              <ul className="guide-step-items">
                {current.items.map((item) => (
                  <li key={item.block} className="guide-chip">
                    {thumbnails.has(item.block) && <img src={thumbnails.get(item.block)} alt="" width={22} height={22} />}
                    <span><RubyText text={item.label ?? blockLabel(item.block)} /></span>
                    <b>×{item.count}</b>
                  </li>
                ))}
              </ul>
            )}
            {current.ops && current.ops.length > 0 && (
              <ol className="guide-ops">
                {current.ops.map((op, index) => (
                  <li key={index}><RubyText text={op} /></li>
                ))}
              </ol>
            )}
            {current.command && <CommandHero command={current.command} />}
            {current.check && (
              <div className="guide-check">
                <p className="guide-box-label">✓ できたかな？</p>
                <p><RubyText text={current.check} /></p>
              </div>
            )}
            {current.trouble && (
              <div className="guide-trouble">
                <p className="guide-box-label">？ こまったら</p>
                <p><RubyText text={current.trouble} /></p>
              </div>
            )}
          </div>
        )}

        {step > total && (
          <div className="guide-complete">
            <p className="guide-complete-emoji" aria-hidden="true">🎉</p>
            <h3 className="guide-title">かんせい！</h3>
            <p className="guide-note">きょうの ミッションは クリア！<br />じぶんの ワールドと くらべてみよう。</p>
            <button type="button" className="guide-secondary" onClick={onExit}>じゆうに みる</button>
            <button type="button" className="guide-tertiary" onClick={handleReset}>さいしょから もういちど</button>
          </div>
        )}
      </div>

      <div className="guide-nav">
        {step > (hasItems ? 0 : 1) && step <= total + 1 && (
          <button type="button" className="guide-back" onClick={() => onNavigate(step - 1)}>
            ← もどる
          </button>
        )}
        {step === 0 && (
          <button
            type="button"
            className={`guide-next${remaining === 0 ? ' is-ready' : ''}`}
            onClick={advanceFromItems}
          >
            {remaining === 0 ? 'ぜんぶ あつめた！ つぎへ →' : 'あつめたら つぎへ →'}
          </button>
        )}
        {step >= 1 && step <= total && (
          <button type="button" className="guide-next is-ready" onClick={() => onNavigate(step + 1)}>
            {step === total ? 'ぜんぶ できた！ →' : 'できた！ つぎへ →'}
          </button>
        )}
      </div>
    </section>
  );
}
